(function () {
  'use strict';

  var STORAGE_KEY = 'makeen:favorites:v1';
  var CHANGE_EVENT = 'makeen:favorites:changed';
  var REMOVE_EVENT = 'makeen:favorites:remove';
  var MAX_ITEMS = 100;
  var memoryHandles = [];

  function normalizeHandle(value) {
    if (typeof value !== 'string') return null;
    var handle = value.trim().toLowerCase();
    return /^[a-z0-9][a-z0-9-]{0,254}$/.test(handle) ? handle : null;
  }

  function uniqueHandles(values) {
    var result = [];
    (Array.isArray(values) ? values : []).forEach(function (value) {
      var handle = normalizeHandle(value);
      if (handle && result.indexOf(handle) === -1 && result.length < MAX_ITEMS) result.push(handle);
    });
    return result;
  }

  function read() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return memoryHandles.slice();
      var parsed = JSON.parse(raw);
      memoryHandles = uniqueHandles(parsed && parsed.version === 1 ? parsed.handles : []);
    } catch (error) {
      // Privacy modes can disable localStorage; keep favorites for this page view.
    }
    return memoryHandles.slice();
  }

  function write(handles, source) {
    memoryHandles = uniqueHandles(handles);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
        version: 1,
        handles: memoryHandles,
        updatedAt: new Date().toISOString()
      }));
    } catch (error) {
      // The in-memory fallback remains usable when persistence is blocked.
    }
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, {
      detail: { handles: memoryHandles.slice(), count: memoryHandles.length, source: source }
    }));
  }

  var store = {
    list: read,
    count: function () { return read().length; },
    has: function (handle) { return read().indexOf(normalizeHandle(handle)) !== -1; },
    add: function (handle) {
      handle = normalizeHandle(handle);
      if (!handle || store.has(handle)) return;
      write([handle].concat(read()), 'add');
    },
    toggle: function (handle) {
      handle = normalizeHandle(handle);
      if (!handle) return false;
      var handles = read();
      var index = handles.indexOf(handle);
      if (index === -1) {
        handles.unshift(handle);
        write(handles, 'add');
        return true;
      }
      handles.splice(index, 1);
      write(handles, 'remove');
      return false;
    },
    remove: function (handle) {
      handle = normalizeHandle(handle);
      var handles = read();
      if (!handle || handles.indexOf(handle) === -1) return;
      write(handles.filter(function (item) { return item !== handle; }), 'remove');
    }
  };

  function FavoriteButton() { return Reflect.construct(HTMLElement, [], FavoriteButton); }
  FavoriteButton.prototype = Object.create(HTMLElement.prototype);
  FavoriteButton.prototype.constructor = FavoriteButton;
  Object.setPrototypeOf(FavoriteButton, HTMLElement);
  FavoriteButton.prototype.connectedCallback = function () {
    var self = this;
    this.button = this.querySelector('button');
    this.onClick = function (event) {
      event.preventDefault();
      event.stopPropagation();
      store.toggle(self.dataset.productHandle);
      self.sync();
    };
    this.onChange = function () { self.sync(); };
    if (this.button) this.button.addEventListener('click', this.onClick);
    window.addEventListener(CHANGE_EVENT, this.onChange);
    this.sync();
  };
  FavoriteButton.prototype.disconnectedCallback = function () {
    if (this.button) this.button.removeEventListener('click', this.onClick);
    window.removeEventListener(CHANGE_EVENT, this.onChange);
  };
  FavoriteButton.prototype.sync = function () {
    if (!this.button) return;
    var active = store.has(this.dataset.productHandle);
    this.button.classList.toggle('is-favorite', active);
    this.button.setAttribute('aria-pressed', String(active));
    this.button.setAttribute('aria-label', active ? this.dataset.labelRemove : this.dataset.labelAdd);
    this.button.setAttribute('data-original-title', active ? 'Favorit entfernen' : 'Favorit');
  };

  function FavoritesLink() { return Reflect.construct(HTMLElement, [], FavoritesLink); }
  FavoritesLink.prototype = Object.create(HTMLElement.prototype);
  FavoritesLink.prototype.constructor = FavoritesLink;
  Object.setPrototypeOf(FavoritesLink, HTMLElement);
  FavoritesLink.prototype.connectedCallback = function () {
    var self = this;
    this.link = this.querySelector('a');
    this.counter = this.querySelector('[data-favorites-count]');
    this.onChange = function () { self.sync(); };
    window.addEventListener(CHANGE_EVENT, this.onChange);
    this.sync();
  };
  FavoritesLink.prototype.disconnectedCallback = function () {
    window.removeEventListener(CHANGE_EVENT, this.onChange);
  };
  FavoritesLink.prototype.sync = function () {
    var count = store.count();
    if (this.counter) {
      this.counter.textContent = count > 99 ? '99+' : String(count);
      this.counter.hidden = count === 0;
    }
    if (this.link) {
      var label = count === 0 ? this.dataset.labelEmpty : count === 1 ? this.dataset.labelOne : (this.dataset.labelMany || '').replace('__COUNT__', count);
      this.link.setAttribute('aria-label', label || 'Favoriten');
    }
  };

  function createElement(name, className, text) {
    var element = document.createElement(name);
    if (className) element.className = className;
    if (typeof text === 'string') element.textContent = text;
    return element;
  }

  function formatPrice(cents, currency) {
    try {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: currency || 'EUR' }).format(Number(cents || 0) / 100);
    } catch (error) {
      return (Number(cents || 0) / 100).toFixed(2).replace('.', ',') + ' €';
    }
  }

  function buildCard(product, currency) {
    var item = createElement('li', 'makeen-favorites-page__item');
    item.dataset.favoriteHandle = product.handle;
    var card = createElement('article', 'makeen-favorites-card');
    var link = createElement('a', 'makeen-favorites-card__image-link');
    link.href = product.url || '/products/' + encodeURIComponent(product.handle);
    var image = createElement('img', 'makeen-favorites-card__image');
    image.src = product.featured_image || (product.images && product.images[0]) || '';
    image.alt = product.title || '';
    image.loading = 'lazy';
    link.appendChild(image);
    var content = createElement('div', 'makeen-favorites-card__content');
    var titleLink = createElement('a', 'makeen-favorites-card__title', product.title || 'Produkt');
    titleLink.href = link.href;
    var price = createElement('p', 'makeen-favorites-card__price', formatPrice(product.price, currency));
    var remove = createElement('button', 'makeen-favorites-card__remove');
    remove.type = 'button';
    remove.setAttribute('aria-label', (product.title || 'Produkt') + ' aus Favoriten entfernen');
    remove.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3m3 0-1 13H7L6 7m4 4v5m4-5v5"/></svg>';
    remove.addEventListener('click', function () {
      remove.dispatchEvent(new CustomEvent(REMOVE_EVENT, {
        bubbles: true,
        composed: true,
        detail: { handle: product.handle, title: product.title || 'Produkt' }
      }));
    });
    card.appendChild(remove);
    content.appendChild(titleLink);
    content.appendChild(price);
    card.appendChild(link);
    card.appendChild(content);
    item.appendChild(card);
    return item;
  }

  function FavoritesPage() { return Reflect.construct(HTMLElement, [], FavoritesPage); }
  FavoritesPage.prototype = Object.create(HTMLElement.prototype);
  FavoritesPage.prototype.constructor = FavoritesPage;
  Object.setPrototypeOf(FavoritesPage, HTMLElement);
  FavoritesPage.prototype.connectedCallback = function () {
    var self = this;
    this.grid = this.querySelector('[data-favorites-grid]');
    this.empty = this.querySelector('[data-favorites-empty]');
    this.loading = this.querySelector('[data-favorites-loading]');
    this.error = this.querySelector('[data-favorites-error]');
    this.retry = this.querySelector('[data-favorites-retry]');
    this.countLabel = this.querySelector('[data-favorites-page-count]');
    this.toast = this.querySelector('[data-favorites-toast]');
    this.toastMessage = this.querySelector('[data-favorites-toast-message]');
    this.undo = this.querySelector('[data-favorites-undo]');
    this.renderVersion = 0;
    this.onChange = function () { self.render(); };
    this.onRetry = function () { self.render(); };
    this.onRemove = function (event) { self.removeFavorite(event.detail); };
    this.onUndo = function () { self.undoRemoval(); };
    window.addEventListener(CHANGE_EVENT, this.onChange);
    this.addEventListener(REMOVE_EVENT, this.onRemove);
    if (this.retry) this.retry.addEventListener('click', this.onRetry);
    if (this.undo) this.undo.addEventListener('click', this.onUndo);
    this.render();
  };
  FavoritesPage.prototype.disconnectedCallback = function () {
    window.removeEventListener(CHANGE_EVENT, this.onChange);
    this.removeEventListener(REMOVE_EVENT, this.onRemove);
    if (this.retry) this.retry.removeEventListener('click', this.onRetry);
    if (this.undo) this.undo.removeEventListener('click', this.onUndo);
    window.clearTimeout(this.toastTimer);
  };
  FavoritesPage.prototype.syncCount = function (count) {
    if (!this.countLabel) return;
    this.countLabel.textContent = count === 1 ? '1 Artikel' : count + ' Artikel';
  };
  FavoritesPage.prototype.removeFavorite = function (detail) {
    if (!detail || !detail.handle) return;
    this.undoHandle = detail.handle;
    store.remove(detail.handle);
    if (this.toastMessage) this.toastMessage.textContent = 'Aus Favoriten entfernt';
    if (this.toast) this.toast.hidden = false;
    window.clearTimeout(this.toastTimer);
    var self = this;
    this.toastTimer = window.setTimeout(function () {
      if (self.toast) self.toast.hidden = true;
      self.undoHandle = null;
    }, 5000);
  };
  FavoritesPage.prototype.undoRemoval = function () {
    if (!this.undoHandle) return;
    store.add(this.undoHandle);
    this.undoHandle = null;
    window.clearTimeout(this.toastTimer);
    if (this.toast) this.toast.hidden = true;
  };
  FavoritesPage.prototype.show = function (state) {
    if (this.grid) this.grid.hidden = state !== 'grid';
    if (this.empty) this.empty.hidden = state !== 'empty';
    if (this.loading) this.loading.hidden = state !== 'loading';
    if (this.error) this.error.hidden = state !== 'error';
  };
  FavoritesPage.prototype.render = function () {
    var self = this;
    var renderVersion = ++this.renderVersion;
    var handles = store.list();
    this.syncCount(handles.length);
    if (!handles.length) {
      if (this.grid) this.grid.replaceChildren();
      this.show('empty');
      return;
    }
    this.show('loading');
    Promise.all(handles.map(function (handle) {
      return fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
        .then(function (response) {
          if (response.status === 404) return { stale: handle };
          if (!response.ok) throw new Error('Produkt konnte nicht geladen werden');
          return response.json();
        })
        .catch(function () { return { failed: handle }; });
    })).then(function (results) {
      if (renderVersion !== self.renderVersion) return;
      if (!self.grid) return;
      self.grid.replaceChildren();
      var failedCount = 0;
      results.forEach(function (result) {
        if (result.stale) store.remove(result.stale);
        else if (result.failed) failedCount += 1;
        else self.grid.appendChild(buildCard(result, self.dataset.currency));
      });
      if (renderVersion !== self.renderVersion) return;
      if (self.grid.children.length) self.show('grid');
      else if (failedCount) self.show('error');
      else self.show('empty');
    });
  };

  if (!customElements.get('favorite-button')) customElements.define('favorite-button', FavoriteButton);
  if (!customElements.get('favorites-link')) customElements.define('favorites-link', FavoritesLink);
  if (!customElements.get('favorites-page')) customElements.define('favorites-page', FavoritesPage);

  window.addEventListener('storage', function (event) {
    if (event.key === STORAGE_KEY || event.key === null) {
      memoryHandles = read();
      window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { handles: memoryHandles, count: memoryHandles.length, source: 'storage' } }));
    }
  });
})();
